import { db } from "../lib/firebase";
import { updateCollectionVersion } from "./versionService";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";

const COLLECTION = "images";

/**
 * Compress an image file to a specified max width and quality, returning a
 * base64 data-URL.
 *
 * @param {File}   file      - Image file from an <input type="file">.
 * @param {number} [maxWidth=800] - Maximum width in pixels.
 * @param {number} [quality=0.7]  - JPEG quality 0‑1.
 * @returns {Promise<string>} Base64 data-URL.
 */
export const compressImage = (file, maxWidth = 800, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

/**
 * Upload an image to Firestore.
 *
 * Accepts either a `file` (File object → compressed to base64) or a `url`
 * string. Returns the created document.
 *
 * @param {{ file?: File, url?: string }} params
 * @returns {Promise<{id: string, type: string, content: string}>}
 */
export const uploadImage = async ({ file, url }) => {
  let content, type;
  if (file) {
    content = await compressImage(file);
    type = "base64";
  } else if (url) {
    content = url;
    type = "url";
  } else {
    throw new Error("Either `file` or `url` is required");
  }

  const docRef = await addDoc(collection(db, COLLECTION), {
    type,
    content,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await updateCollectionVersion("images");
  return { id: docRef.id, type, content };
};

/**
 * Retrieve all images ordered by creation date (newest first).
 *
 * @returns {Promise<Array>}
 */
export const getAllImages = async () => {
  const q = query(collection(db, COLLECTION), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Delete an image document by id.
 *
 * @param {string} id
 */
export const deleteImage = async (id) => {
  await deleteDoc(doc(db, COLLECTION, id));
  await updateCollectionVersion("images");
};

/**
 * Update an image document's data.
 *
 * @param {string} id
 * @param {object} data
 */
export const updateImage = async (id, data) => {
  await updateDoc(doc(db, COLLECTION, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};
